// FHIR and SMART Types
export interface FhirClient {
  patient: {
    id: string
    read: () => Promise<FhirPatient>
  }
  user: {
    id: string
    read: () => Promise<FhirPractitioner>
  }
  request: (url: string, options?: RequestInit) => Promise<unknown>
  getState: (key: string) => string | undefined
}

export interface FhirPatient {
  resourceType: 'Patient'
  id: string
  name?: Array<{
    given?: string[]
    family?: string
    use?: string
  }>
  birthDate?: string
  gender?: string
}

export interface FhirPractitioner {
  resourceType: 'Practitioner'
  id: string
  name?: Array<{
    given?: string[]
    family?: string
    prefix?: string[]
  }>
}

// Application Types
// Production classifier values (M7+) plus legacy values for backward compat
export type MessageCategory =
  | 'refill'
  | 'test_results'
  | 'clinical_question'
  | 'appointment'
  | 'administrative'
  | 'urgent'
  | 'unclassified'
  // Legacy values (pre-M7 demo classifier)
  | 'medication'
  | 'lab_result'
  | 'referral'
  | 'general'

export type MessageSubcategory =
  | 'lab_test_order'
  | 'new_problem'
  | 'recurring_problem'
  | 'medication_refill'
  | 'results_interpretation'
  | 'appointment_scheduling'
  | 'rx_renewal'
  | 'patient_call'

export type MessagePriority = 'low' | 'normal' | 'high' | 'urgent'

export type MessageStatus =
  | 'unread'
  | 'read'
  | 'in_progress'
  | 'pending_review'
  | 'completed'
  | 'archived'

export type DraftStatus = 'none' | 'generating' | 'ready' | 'approved' | 'rejected'

export interface Message {
  id: string
  patientId: string
  patientName: string
  providerId?: string
  providerName?: string
  category: MessageCategory
  priority: MessagePriority
  status: MessageStatus
  subject: string
  content: string
  receivedAt: string
  updatedAt: string
  requiresProviderAction: boolean
  hasAIDraft: boolean
  draftStatus: DraftStatus
  csDetected: boolean
  csConfidence?: number
  tags?: string[]
  subcategory?: MessageSubcategory
  hasHighRiskSignal?: boolean
}

// Protocol Evaluation (M7 Rx Standard Work)
export interface ProtocolStep {
  step_name: string
  code: string
  passed: boolean | null
  detail: string
  phi_context: Record<string, unknown> | null
}

export interface ProtocolEvaluation {
  evaluation_id: string
  protocol_id: string
  protocol_version: string
  can_auto_queue: boolean
  requires_manual_review: boolean
  steps: ProtocolStep[]
  created_at: string
}

export interface MessageDraft {
  id: string
  messageId: string
  content: string
  generatedAt: string
  reasoning: string
  confidence: number
  suggestedActions?: string[]
}

export interface MessageFilter {
  category?: MessageCategory
  priority?: MessagePriority
  status?: MessageStatus
  requiresAction?: boolean
  searchQuery?: string
  dateFrom?: string
  dateTo?: string
}

export interface PaginationInfo {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface MessagesListResponse {
  messages: Message[]
  pagination: PaginationInfo
}

export interface MessageDetailResponse {
  message: Message
  draft?: MessageDraft
  relatedMessages?: Message[]
}

// Auth Types
export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  client: FhirClient | null
  patient: FhirPatient | null
  practitioner: FhirPractitioner | null
  accessToken: string | null
  tokenExpiry: Date | null
}

// Store Types
export interface InboxState {
  messages: Message[]
  selectedMessage: Message | null
  currentDraft: MessageDraft | null
  filters: MessageFilter
  pagination: PaginationInfo
  isLoading: boolean
  error: string | null
}

// UI Types
export interface ToastNotification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

export interface ConfirmDialog {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info'
}

// SMART Launch Config
export interface SmartLaunchConfig {
  clientId: string
  scope: string
  redirectUri: string
  iss?: string
}

export const SMART_CONFIG: SmartLaunchConfig = {
  clientId: import.meta.env.VITE_SMART_CLIENT_ID || 'paraclete-app',
  scope: 'launch openid fhirUser patient/*.read user/*.read',
  redirectUri: import.meta.env.VITE_SMART_REDIRECT_URI || `${window.location.origin}/callback`,
}

// Category Display Config
export const CATEGORY_CONFIG: Record<
  MessageCategory,
  { label: string; color: string; bgColor: string }
> = {
  // M7 production classifier values
  refill: { label: 'Refill', color: '#2471a3', bgColor: '#d6eaf8' },
  test_results: { label: 'Test Results', color: '#27ae60', bgColor: '#d5f5e3' },
  clinical_question: { label: 'Clinical Question', color: '#8e44ad', bgColor: '#f4ecf7' },
  appointment: { label: 'Appointment', color: '#7d3c98', bgColor: '#ebdef0' },
  administrative: { label: 'Administrative', color: '#5d6d7e', bgColor: '#eaecee' },
  urgent: { label: 'Urgent', color: '#c0392b', bgColor: '#fadbd8' },
  unclassified: { label: 'Unclassified', color: '#7f8c8d', bgColor: '#ecf0f1' },
  // Legacy values (pre-M7)
  medication: { label: 'Medication', color: '#2471a3', bgColor: '#d6eaf8' },
  lab_result: { label: 'Lab Result', color: '#27ae60', bgColor: '#d5f5e3' },
  referral: { label: 'Referral', color: '#e67e22', bgColor: '#fdebd0' },
  general: { label: 'General', color: '#5d6d7e', bgColor: '#eaecee' },
}

export const SUBCATEGORY_CONFIG: Record<
  MessageSubcategory,
  { label: string; color: string; bgColor: string }
> = {
  lab_test_order: { label: 'Lab Test Order', color: '#1e8449', bgColor: '#e9f7ef' },
  new_problem: { label: 'New Problem', color: '#c0392b', bgColor: '#fdedec' },
  recurring_problem: { label: 'Recurring Problem', color: '#b9770e', bgColor: '#fef9e7' },
  medication_refill: { label: 'Medication Refill', color: '#2471a3', bgColor: '#d6eaf8' },
  results_interpretation: { label: 'Results Interpretation', color: '#117a65', bgColor: '#d1f2eb' },
  appointment_scheduling: { label: 'Appointment Scheduling', color: '#7d3c98', bgColor: '#f4ecf7' },
  rx_renewal: { label: 'Rx Renewal', color: '#1a5276', bgColor: '#d4e6f1' },
  patient_call: { label: 'Patient Call', color: '#a93268', bgColor: '#f9e3ee' },
}

// Priority Display Config
export const PRIORITY_CONFIG: Record<
  MessagePriority,
  { label: string; color: string; icon: string }
> = {
  low: { label: 'Low', color: '#95a5a6', icon: '○' },
  normal: { label: 'Normal', color: '#5d6d7e', icon: '●' },
  high: { label: 'High', color: '#e67e22', icon: '▲' },
  urgent: { label: 'Urgent', color: '#c0392b', icon: '⚠' },
}

// Re-export order types
export * from './orders'

// Re-export result follow-up types
export * from './results'

// Re-export routing types
export * from './routing'

// Re-export differential diagnosis types
export * from './differentials'

// Re-export reasoning chain types
export * from './reasoning'

// Re-export cross-encounter pattern types
export * from './patterns'
